import classNames from 'classnames';
import { setNestedObjectValues, useFormikContext } from 'formik';
import { useEffect, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import Button from '../components/atoms/Button';
import { showDefaultError } from '../components/molecules/Notifications';
import { LOADING_TYPES, useCheckoutState } from './checkoutState';
import { config, getAssetUrl } from './config';
import { pushErrorTrackingEventFromErrorObject } from './tracking';
import useCss from './useCss';
import useCustomTranslations from './useCustomTranslations';
import useDeepCompare from './useDeepCompare';
import useScript from './useScript';
import { wait } from './wait';
import sendDatadogEvent from './sendDatadogEvent';

// https://docs.adyen.com/online-payments/web-components
// https://docs.adyen.com/online-payments/build-your-integration/advanced-flow/?platform=Web&integration=Components&version=5.60.0&tab=embed_script_and_stylesheet_2#add-components

type ButtonActions = { disable: () => void; enable: () => void };
export type AdyenBaseSpecification = {
  paymentMethod: Record<string, unknown>;
  browserInfo?: Record<string, unknown>;
  origin?: string;
  details?: Record<string, unknown>;
  paymentData?: string; // paypal
  storePaymentMethod?: boolean; // creditcard
  checkoutAttemptId?: string;
  clientStateDataIndicator?: boolean;
};

export type AdyenParams = { isValid: boolean; data: AdyenBaseSpecification };

export type AdyenConfig = {
  payButton?: 'adyen' | 'base' | 'google' | 'apple';
  withButtonActionsRef?: boolean; // paypal
  withPaymentMethods?: boolean; // creditcard, googlepay, applepay
  withPspSession?: boolean; // creditcard
  withTranslations?: string; // creditcard
  onAdditionalDetails?: (params: AdyenParams, callbackUrlRef: React.MutableRefObject<string | undefined>) => void; // paypal
  onChange?: (onChangeParams: AdyenParams & { canBeRecurring?: boolean }) => void; // creditcard, onlineBanking_PL
  onError?: (e: unknown) => void;
  onSubmit?: (onSubmitParams: AdyenParams) => Promise<void>;
};

export type CardConfig = {
  buttonType?: string; // googlepay
  blockPayPalCreditButton?: boolean; // paypal
  blockPayPalPayLaterButton?: boolean; // paypal
  enableStoreDetails?: boolean; // creditcard
  hasHolderName?: boolean; // creditcard
  holderNameRequired?: boolean; // creditcard
  data?: { holderName?: string }; // creditcard initial values
  onClick?: () => void;
  onChange?: (onChangeParams: AdyenParams & { canBeRecurring?: boolean }) => void; // creditcard
};

type AdyenPaymentProps = {
  name: string;
  adyenConfig: AdyenConfig;
  cardConfig?: CardConfig;
  withActions?: boolean;
  withRecurring?: boolean; // creditcard
};

declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Window {
    AdyenCheckout: any;
  }
}

const isFormValid = (formErrors: any) => JSON.stringify(formErrors) === '{}';

const useAdyen = ({ name, adyenConfig, cardConfig, withActions, withRecurring }: AdyenPaymentProps) => {
  const adyenSdkHost = config.isProduction ? 'checkoutshopper-live.adyen.com' : 'checkoutshopper-test.adyen.com';
  const integrity = 'sha384-6qEAHFy5klBO9QE1zhxuGAKiAe7fVK7SAW7KnLSaYxj2UedBLGHsnNi7NrNcKIQR';
  const scriptLoadingPromise = useScript(`https://${adyenSdkHost}/checkoutshopper/sdk/5.65.0/adyen.js`, integrity);
  useCss(`https://${adyenSdkHost}/checkoutshopper/sdk/5.65.0/adyen.css`);

  const customTranslations = useCustomTranslations();
  const adyenComponentRef = useRef<any>();
  const callbackUrlRef = useRef<string>();
  const { checkoutState, isLoading, setLoading, setPollingEnabled, adyenSpecificationPayloadRef } = useCheckoutState();
  const { values, setTouched, submitForm, isSubmitting, validateForm } = useFormikContext<{ paymentMethod: string }>();

  const selectedPaymentOption = checkoutState.orderState.payment?.selected?.[0];

  const isRecurring = !!selectedPaymentOption?.recurringPaymentOptionId;
  const canBeStored = selectedPaymentOption?.configuration?.isRecurring;
  const canBeRecurring = !isRecurring && canBeStored;

  const initializationData = useDeepCompare(selectedPaymentOption?.configuration?.initializationData);
  const buttonActionsRef = useRef<ButtonActions>();

  const { payButton, withButtonActionsRef, withPaymentMethods, withPspSession, withTranslations } = adyenConfig;
  const { onChange, onSubmit, onAdditionalDetails } = adyenConfig;

  const updateAdyenButtonState = () => {
    // Paypal is the only one using "adyen" button so we control it with a ref
    if (!buttonActionsRef.current || !withButtonActionsRef) {
      return;
    }

    (async () => {
      const formikErrors = await validateForm?.(values);
      if (!isFormValid(formikErrors) || (isLoading && isLoading !== LOADING_TYPES.adyenLoad)) {
        buttonActionsRef.current!.disable();
      } else {
        buttonActionsRef.current!.enable();
      }
    })();
  };

  const { paymentOptionKey, recurringPaymentOptionId } = selectedPaymentOption ?? {};
  const formAndStateNotMatching = values.paymentMethod !== (recurringPaymentOptionId ?? paymentOptionKey);
  const isAdyenPayPalLoading = paymentOptionKey === 'adyen_paypal' && isLoading === LOADING_TYPES.paymentSubmit;

  useEffect(() => {
    if (!paymentOptionKey || !initializationData) {
      return;
    }

    if (formAndStateNotMatching) {
      return;
    }

    const requiresBrands = selectedPaymentOption?.configuration?.public?.requiresBrands;
    const requiresTranslation = selectedPaymentOption?.configuration?.public?.requiresTranslation && withTranslations;

    const { action, callbackUrl, clientKey, sessionId, pspSessionId, sessionData } = initializationData;

    if (withActions && adyenComponentRef.current && callbackUrl && action) {
      // Actions are enabled and available to handle
      callbackUrlRef.current = callbackUrl;
      const adyenComponent = adyenComponentRef.current;
      adyenComponent.handleAction(action);
      return;
    }
    (async () => {
      if (isLoading !== LOADING_TYPES.paymentSubmit) {
        setLoading(LOADING_TYPES.adyenLoad);
      }
      await scriptLoadingPromise.current;
      const formattedLocale = config.locale.replace('_', '-');
      const configuration = {
        environment: config.isProduction ? 'live' : 'test',
        clientKey,
        session: { id: withPspSession ? pspSessionId : sessionId, sessionData },
        locale: formattedLocale,
        ...(payButton && { showPayButton: payButton === 'adyen' }),
        ...(withPaymentMethods && { paymentMethodsResponse: initializationData.paymentMethodsResponse }),
        ...(requiresTranslation && { translations: { [formattedLocale]: customTranslations[withTranslations] } }),
        ...(onChange && { onChange: (params: AdyenParams) => onChange({ ...params, canBeRecurring }) }),
        onSubmit: async (params: AdyenParams) => {
          if (!config.environment?.includes('live')) {
            console.log(paymentOptionKey, params);
          }

          if (paymentOptionKey === 'adyen_paypal') {
            sendDatadogEvent('checkout-load', undefined, params);
          }

          if (onSubmit) {
            onSubmit(params);
          } else {
            // Default adyen submit flow saving widget data in adyenSpecificationPayloadRef
            const { paymentMethod, origin, browserInfo } = params.data;
            adyenSpecificationPayloadRef.current = { paymentMethod, origin, browserInfo };

            await wait(0);
            submitForm();
          }
        },
        onAdditionalDetails: (params: AdyenParams) => {
          if (onAdditionalDetails) {
            onAdditionalDetails(params, callbackUrlRef);
          }
        },
        onError: (error: any, component: unknown) => {
          if (!config.environment?.includes('live') || paymentOptionKey === 'spay_adyen_apple_pay') {
            console.log(`Adyen onError for ${paymentOptionKey}`, error);
          }

          console.error(error.name, error.message, error.stack, component);
          pushErrorTrackingEventFromErrorObject(error, paymentOptionKey);
          setPollingEnabled(true);
          setLoading(LOADING_TYPES.off);
        }
      };

      try {
        const checkout = await window.AdyenCheckout(configuration);

        const getStoredCard = () =>
          checkout.paymentMethodsResponse.storedPaymentMethods.find(
            ({ id }: { id: string }) => initializationData.storedPaymentMethodId === id
          );

        const cardConfiguration = {
          onInit: (_: unknown, actions: ButtonActions) => {
            buttonActionsRef.current = actions;
            updateAdyenButtonState();
          },
          ...(withButtonActionsRef && { onClick: () => setTouched(setNestedObjectValues(values, true)) }),
          ...(withRecurring && isRecurring ? getStoredCard() : { enableStoreDetails: canBeStored }),
          ...(requiresBrands && { brands: requiresBrands }),
          ...cardConfig
        };

        const adyenComponent = await checkout.create(name, cardConfiguration);
        await adyenComponent.isAvailable?.();
        adyenComponent.mount('#adyen-payment-container');
        adyenComponentRef.current = adyenComponent;
      } catch (e) {
        if (!config.environment?.includes('live')) {
          console.log(`Adyen initialisation failed for ${paymentOptionKey}`);
        }
        console.error(e);
        showDefaultError();
      }
      if (isLoading !== LOADING_TYPES.paymentSubmit) {
        setLoading(LOADING_TYPES.off);
      }
    })();
  }, [initializationData]);

  useEffect(updateAdyenButtonState, [values, isLoading]);

  useEffect(() => {
    if (IS_WEB_COMPONENT && !import.meta.env.DEV) {
      const adyenPaypalContainer = document.getElementById('adyen-payment-container');

      if (!adyenPaypalContainer) {
        return;
      }

      if (isAdyenPayPalLoading) {
        adyenPaypalContainer.style.display = 'none';
      } else {
        adyenPaypalContainer.removeAttribute('style');
      }
    }
  }, [isAdyenPayPalLoading]);

  useEffect(
    () => () => {
      const adyenComponent = adyenComponentRef.current;
      if (adyenComponent) {
        adyenComponent.unmount();
      }
    },
    []
  );

  if (formAndStateNotMatching) {
    return (
      payButton && (
        <Button
          disabled
          type="button"
          className={classNames('navigation-section__next-step', { 'cursor-disabled': !!isLoading })}
        >
          <FormattedMessage id="payment.payNow" />
        </Button>
      )
    );
  }

  const onBaseButtonClick = async () => {
    // Payment methods with non "adyen" button (custom button)
    const formikErrors = await setTouched(setNestedObjectValues(values, true));
    if (!isLoading && isFormValid(formikErrors)) {
      try {
        setPollingEnabled(false);
        setLoading(LOADING_TYPES.adyenLoad);
        await adyenComponentRef.current!.submit();
      } catch (error) {
        setLoading(LOADING_TYPES.off);
      }
    }
  };

  return (
    <>
      {IS_WEB_COMPONENT && !import.meta.env.DEV ?
        <slot name="adyen-payment-container" />
      : <div
          id="adyen-payment-container"
          className={classNames({
            'navigation-section__hide-paypal-button': isAdyenPayPalLoading
          })}
        />
      }
      {isAdyenPayPalLoading && (
        <Button
          disabled={true}
          isLoading={true}
          data-test-id="paypal-button-loading"
          className="navigation-section__next-step cursor-disabled"
        />
      )}
      {payButton && payButton !== 'adyen' && (
        <Button
          disabled={isSubmitting}
          isLoading={isLoading === LOADING_TYPES.adyenLoad || isSubmitting}
          onClick={onBaseButtonClick}
          type="button"
          data-test-id="navigation-next-step"
          className={classNames('navigation-section__next-step', {
            [`navigation-section__next-step__${payButton}`]: payButton !== 'base',
            'cursor-disabled': !!isLoading
          })}
        >
          {payButton === 'base' ?
            <FormattedMessage id="payment.payNow" />
          : <img
              className={`navigation-section__next-step__img__${payButton}`}
              src={getAssetUrl(`payment/${payButton}.svg`)}
              key={payButton}
            />
          }
        </Button>
      )}
    </>
  );
};

export default useAdyen;
